import React, {
  useMemo,
  useState,
  useRef,
  useLayoutEffect,
  useCallback,
  type CSSProperties,
} from 'react';
import classNames from 'classnames';
import { Button } from '@plataforma-xvia/design-system-es';
// @ts-ignore
import renderHTML from 'react-render-html';
import { CatalogDetail } from '../../../apis/detail-catalog';
import { SectionDetails } from './section-details';
import styles from '../styles.module.css';

export type TabKey = 'steps' | 'info' | 'sections';

interface TabSectionProps {
  tabs: Array<{ id: TabKey; label: string }>;
  activeTab: TabKey;
  onTabChange: (tab: TabKey) => void;
  steps?: CatalogDetail['steps'];
  info?: CatalogDetail['info'];
  departmentSections?: CatalogDetail['departmentSections'];
}

export const TabSection: React.FC<TabSectionProps> = ({
  tabs,
  activeTab,
  onTabChange,
  steps,
  info,
  departmentSections,
}) => {
  const TAB_INDICATOR_WIDTH = 224;
  const TAB_GAP = 32;

  const activeTabIndex = useMemo(
    () => tabs.findIndex((tab) => tab.id === activeTab),
    [tabs, activeTab]
  );
  const safeActiveIndex = activeTabIndex >= 0 ? activeTabIndex : 0;

  const tabListRef = useRef<HTMLDivElement | null>(null);
  const tabRefs = useRef<(HTMLDivElement | null)[]>([]);

  const [indicatorMetrics, setIndicatorMetrics] = useState({
    width: TAB_INDICATOR_WIDTH,
    offset: safeActiveIndex * (TAB_INDICATOR_WIDTH + TAB_GAP),
  });

  const updateIndicatorMetrics = useCallback(() => {
    const listEl = tabListRef.current;
    if (!listEl) {
      return;
    }

    const isCompact = window.innerWidth <= 640;

    if (isCompact) {
      const listWidth = listEl.clientWidth;
      const width = listWidth / tabs.length;
      const offset = width * safeActiveIndex;
      setIndicatorMetrics({ width, offset });
      return;
    }

    const activeButtonWrapper = tabRefs.current[safeActiveIndex];
    if (activeButtonWrapper) {
      const activeRect = activeButtonWrapper.getBoundingClientRect();
      const listRect = listEl.getBoundingClientRect();
      const width = activeRect.width;
      const offset = activeRect.left - listRect.left;
      setIndicatorMetrics({ width, offset });
      return;
    }

    setIndicatorMetrics({
      width: TAB_INDICATOR_WIDTH,
      offset: safeActiveIndex * (TAB_INDICATOR_WIDTH + TAB_GAP),
    });
  }, [safeActiveIndex, tabs.length]);

  useLayoutEffect(() => {
    updateIndicatorMetrics();
  }, [updateIndicatorMetrics, safeActiveIndex, tabs.length]);

  useLayoutEffect(() => {
    const handleResize = () => updateIndicatorMetrics();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [updateIndicatorMetrics]);

  const indicatorStyle = useMemo(
    () =>
      ({
        width: `${indicatorMetrics.width}px`,
        transform: `translateX(${indicatorMetrics.offset}px)`,
      } as CSSProperties),
    [indicatorMetrics]
  );

  const tabContent = useMemo(() => {
    switch (activeTab) {
      case 'steps':
        if (!steps || steps.length === 0) {
          return (
            <p className={styles.emptyMessage}>
              Nenhuma etapa disponível para este serviço.
            </p>
          );
        }

        return (
          <div className={styles.stepContent}>
            {steps
              .slice()
              .sort((a, b) => a.orderIndex - b.orderIndex)
              .map((step) => (
                <div key={step.id} className={styles.stepItem}>
                  <div className={styles.stepHeader}>
                    <span className={styles.stepNumber}>{step.orderIndex}</span>
                    <h3 className={styles.stepTitle}>{step.name}</h3>
                  </div>

                  {step.description && (
                    <p className={styles.stepSummary}>{step.description}</p>
                  )}

                  {step.htmlContent && (
                    <div className={styles.stepRichText}>
                      {renderHTML(step.htmlContent)}
                    </div>
                  )}

                  {!!step.documents.length && (
                    <div className={styles.documents}>
                      <h4 className={styles.documentsTitle}>
                        Documentos necessários
                      </h4>
                      <ul className={styles.documentsList}>
                        {step.documents.map((doc, docIndex) => (
                          <li key={docIndex}>{doc}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ))}
          </div>
        );
      case 'info':
        if (!info || info.length === 0) {
          return (
            <p className={styles.emptyMessage}>
              Nenhuma orientação disponível para este serviço.
            </p>
          );
        }

        return (
          <div className={styles.infoContent}>
            {info
              .slice()
              .sort((a, b) => a.orderIndex - b.orderIndex)
              .map((item) => (
                <div key={item.id} className={styles.infoItem}>
                  <h3 className={styles.infoTitle}>{item.title}</h3>
                  <div className={styles.infoText}>{renderHTML(item.text)}</div>
                </div>
              ))}
          </div>
        );
      case 'sections':
        if (!departmentSections || departmentSections.length === 0) {
          return (
            <p className={styles.emptyMessage}>
              Nenhuma unidade disponível para este serviço.
            </p>
          );
        }

        return (
          <div className={styles.sectionsContent}>
            {departmentSections.map((section) => (
              <div className={styles.sectionDetails} key={section.id || section.name}>
                <SectionDetails section={section} showMap={false} />
              </div>
            ))}
          </div>
        );
      default:
        return null;
    }
  }, [activeTab, departmentSections, info, steps]);

  return (
    <div className={styles.tabSection}>
      <div className={styles.tabList} ref={tabListRef}>
        {tabs.map((tab, index) => {
          const isActive = activeTab === tab.id;

          return (
            <div
              key={tab.id}
              ref={(el) => (tabRefs.current[index] = el)}
              className={styles.tabButtonWrapper}
            >
              <Button
                type="button"
                onClick={() => onTabChange(tab.id)}
                className={classNames(styles.tabButton, {
                  [styles.tabButtonActive]: isActive,
                })}
                variant="text"
                size="none"
                aria-pressed={isActive}
                iconStart={<span className={styles.tabOrder}>{index + 1}</span>}
                label={tab.label}
              />
            </div>
          );
        })}
        <span
          className={styles.tabIndicator}
          style={indicatorStyle}
          aria-hidden
        />
      </div>
      <div className={styles.tabPanel}>{tabContent}</div>
    </div>
  );
};
